import { createFileRoute, Link } from "@tanstack/react-router"
import {
  Activity,
  ArrowRight,
  Bot,
  Check,
  Copy,
  Cpu,
  PiggyBank,
  Plug,
  Plus,
  Server,
  Sparkles,
  Wand2,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useAgents, useBudgetStatus, useBudgets } from "@/api/queries/agents"
import { useMcpServers } from "@/api/queries/mcp"
import { useModels, useProviders } from "@/api/queries/providers"
import { useRuns } from "@/api/queries/runs"
import { useSession } from "@/api/queries/session"
import { useSkills } from "@/api/queries/skills"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { StatCard } from "@/components/app/stat-card"
import { EmptyState, ErrorState } from "@/components/app/states"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { formatCost, formatTokens } from "@/features/runs/format"
import { RunStatusBadge, useSourceLabel } from "@/features/runs/run-status"
import { AgentAvatar } from "@/features/studio/agent-avatar"
import { BudgetStatusCard } from "@/features/studio/budget-status"
import { CopySettingsDialog } from "@/features/studio/copy-settings-dialog"
import { formatNumber } from "@/lib/format"
import { can } from "@/lib/permissions"
import { cn } from "@/lib/utils"

export const Route = createFileRoute("/_app/studio/")({
  staticData: { titleKey: "studio.nav.overview" },
  component: StudioOverview,
})

function SetupSteps({ providers, models, agents }: { providers: number; models: number; agents: number }) {
  const { t } = useTranslation()
  const steps = [
    {
      done: providers > 0,
      title: t("studio.overview.setup.provider"),
      to: "/studio/providers",
      search: { new: true },
    },
    { done: models > 0, title: t("studio.overview.setup.model"), to: "/studio/models", search: {} },
    {
      done: agents > 0,
      title: t("studio.overview.setup.agent"),
      to: "/studio/agents",
      search: { new: true },
    },
  ] as const
  if (steps.every((s) => s.done)) return null
  const next = steps.findIndex((s) => !s.done)
  return (
    <section className="bg-card mb-8 rounded-xl border p-4 sm:p-5" aria-labelledby="setup-title">
      <h2 id="setup-title" className="text-sm font-semibold">
        {t("studio.overview.setup.title")}
      </h2>
      <p className="text-muted-foreground mt-0.5 text-xs">{t("studio.overview.setup.description")}</p>
      <ol className="mt-4 grid gap-2 sm:grid-cols-3">
        {steps.map((s, i) => (
          <li key={s.to}>
            <Link
              to={s.to}
              search={s.search}
              className={cn(
                "hover:bg-muted/50 flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors",
                i === next && "border-primary/40 bg-primary/[0.03]",
              )}
            >
              <span
                className={cn(
                  "flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium tabular-nums",
                  s.done && "border-success/40 bg-success/10 text-success",
                  i === next && "border-primary text-primary",
                )}
              >
                {s.done ? <Check className="size-3.5" /> : i + 1}
              </span>
              <span className={cn("min-w-0 flex-1", s.done && "text-muted-foreground line-through")}>
                {s.title}
              </span>
              {i === next && <ArrowRight className="text-primary size-4" />}
            </Link>
          </li>
        ))}
      </ol>
    </section>
  )
}

function RecentRuns() {
  const { t } = useTranslation()
  const sourceLabel = useSourceLabel()
  const runs = useRuns({ limit: 8, offset: 0 })
  return (
    <section className="bg-card overflow-hidden rounded-xl border" aria-labelledby="recent-runs">
      <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <h2 id="recent-runs" className="text-sm font-semibold">
          {t("studio.overview.recentRuns")}
        </h2>
        <Button variant="ghost" size="sm" nativeButton={false} render={<Link to="/insights/runs" />}>
          {t("studio.overview.viewAll")}
          <ArrowRight />
        </Button>
      </div>
      {runs.isPending ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      ) : runs.isError ? (
        <ErrorState variant="plain" error={runs.error} onRetry={() => void runs.refetch()} />
      ) : runs.data.items.length === 0 ? (
        <EmptyState variant="plain" size="sm" icon={Activity} title={t("studio.overview.noRuns")} />
      ) : (
        <ul className="divide-y">
          {runs.data.items.map((r) => (
            <li key={r.uid}>
              <Link
                to="/insights/runs"
                search={{ run: r.uid }}
                className="hover:bg-muted/40 flex items-center gap-3 px-4 py-2.5 transition-colors"
              >
                <AgentAvatar name={r.agent_name || "?"} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{r.agent_name}</span>
                    <span className="text-muted-foreground shrink-0 text-xs">{sourceLabel(r.source)}</span>
                  </div>
                  <div className="text-muted-foreground truncate text-xs">{r.prompt_preview || "—"}</div>
                </div>
                <div className="hidden shrink-0 text-right text-xs tabular-nums sm:block">
                  <div>{formatTokens(r.total_tokens)}</div>
                  <div className="text-muted-foreground">{formatCost(r.cost)}</div>
                </div>
                <div className="flex w-24 shrink-0 flex-col items-end gap-1">
                  <RunStatusBadge status={r.status} />
                  <RelativeTime value={r.started_at} className="text-muted-foreground text-[0.7rem]" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function BudgetsPanel() {
  const { t } = useTranslation()
  const status = useBudgetStatus()
  const budgets = useBudgets()
  return (
    <section className="space-y-3" aria-labelledby="budget-status">
      <div className="flex items-center justify-between gap-2">
        <h2 id="budget-status" className="text-sm font-semibold">
          {t("studio.overview.budgets")}
        </h2>
        <Button variant="ghost" size="sm" nativeButton={false} render={<Link to="/studio/budgets" />}>
          {t("studio.overview.manage")}
          <ArrowRight />
        </Button>
      </div>
      {status.isPending ? (
        <Skeleton className="h-24 rounded-xl" />
      ) : status.isError ? (
        <ErrorState variant="card" error={status.error} onRetry={() => void status.refetch()} />
      ) : status.data.length === 0 ? (
        <EmptyState
          size="sm"
          icon={PiggyBank}
          title={t("studio.overview.noBudgets")}
          description={t("studio.overview.noBudgetsHint")}
          action={
            <Button size="sm" variant="outline" nativeButton={false} render={<Link to="/studio/budgets" />}>
              <Plus />
              {t("studio.budgets.new")}
            </Button>
          }
        />
      ) : (
        <div className="grid gap-2">
          {status.data.slice(0, 4).map((s) => (
            <BudgetStatusCard
              key={s.policy_id}
              status={s}
              threshold={budgets.data?.find((b) => b.id === s.policy_id)?.alert_threshold_pct}
              compact
            />
          ))}
        </div>
      )}
    </section>
  )
}

function StudioOverview() {
  const { t } = useTranslation()
  const session = useSession()
  const agents = useAgents({ include_archived: true })
  const models = useModels()
  const providers = useProviders()
  const mcp = useMcpServers()
  const skills = useSkills()
  const [copyOpen, setCopyOpen] = useState(false)
  const active = agents.data?.filter((a) => !a.archived_at) ?? []
  const runs7d = active.reduce((n, a) => n + a.runs_7d, 0)
  const loaded = agents.isSuccess && models.isSuccess && providers.isSuccess

  const links = [
    {
      to: "/studio/agents",
      icon: Bot,
      title: t("nav.items.agents"),
      hint: t("studio.overview.links.agents"),
    },
    {
      to: "/studio/skills",
      icon: Sparkles,
      title: t("nav.items.skills"),
      hint: t("studio.overview.links.skills"),
    },
    { to: "/studio/mcp", icon: Plug, title: t("nav.items.mcp"), hint: t("studio.overview.links.mcp") },
    {
      to: "/studio/models",
      icon: Cpu,
      title: t("nav.items.models"),
      hint: t("studio.overview.links.models"),
    },
    {
      to: "/studio/providers",
      icon: Server,
      title: t("nav.items.providers"),
      hint: t("studio.overview.links.providers"),
    },
    {
      to: "/insights/runs",
      icon: Activity,
      title: t("nav.items.runs"),
      hint: t("studio.overview.links.runs"),
    },
  ] as const

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("studio.overview.title")}
        description={t("studio.overview.description")}
        icon={Wand2}
        actions={
          <>
            {can(session, "admin") && (
              <Button variant="outline" onClick={() => setCopyOpen(true)} data-testid="copy-settings">
                <Copy />
                {t("studio.copy.action")}
              </Button>
            )}
            <Button nativeButton={false} render={<Link to="/studio/agents" search={{ new: true }} />}>
              <Plus />
              {t("studio.agents.new")}
            </Button>
          </>
        }
      />

      {loaded && (
        <SetupSteps providers={providers.data.length} models={models.data.length} agents={active.length} />
      )}

      <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label={t("studio.overview.stats.agents")}
          value={formatNumber(active.length)}
          icon={Bot}
          loading={agents.isPending}
          hint={
            agents.data && agents.data.length > active.length
              ? t("studio.overview.stats.archived", { count: agents.data.length - active.length })
              : undefined
          }
        />
        <StatCard
          label={t("studio.overview.stats.runs7d")}
          value={formatNumber(runs7d)}
          icon={Activity}
          loading={agents.isPending}
        />
        <StatCard
          label={t("studio.overview.stats.models")}
          value={formatNumber(models.data?.length)}
          icon={Cpu}
          loading={models.isPending}
        />
        <StatCard
          label={t("studio.overview.stats.providers")}
          value={formatNumber(providers.data?.length)}
          icon={Server}
          loading={providers.isPending}
        />
        <StatCard
          label={t("studio.overview.stats.integrations")}
          value={formatNumber(mcp.data?.filter((s) => s.enabled).length)}
          icon={Plug}
          loading={mcp.isPending}
        />
        <StatCard
          label={t("studio.overview.stats.skills")}
          value={formatNumber(skills.data?.filter((s) => s.enabled).length)}
          icon={Sparkles}
          loading={skills.isPending}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-6">
          <RecentRuns />
          <section aria-labelledby="quick-links">
            <h2 id="quick-links" className="mb-3 text-sm font-semibold">
              {t("studio.overview.quickLinks")}
            </h2>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="bg-card hover:bg-muted/40 group flex items-start gap-3 rounded-xl border p-3 transition-colors"
                >
                  <span className="bg-muted text-muted-foreground group-hover:text-foreground flex size-8 shrink-0 items-center justify-center rounded-lg">
                    <l.icon className="size-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{l.title}</span>
                    <span className="text-muted-foreground line-clamp-2 block text-xs">{l.hint}</span>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        </div>
        <BudgetsPanel />
      </div>
      <CopySettingsDialog open={copyOpen} onOpenChange={setCopyOpen} />
    </PageContainer>
  )
}
